import Highcharts, { Options } from 'highcharts';
import Highmaps from 'highcharts/highmaps';
import HighchartsReact from 'highcharts-react-official';
import _merge from 'lodash.merge';
import React from 'react';
import ReactResizeDetector from 'react-resize-detector';

import { barFormatter, graphicFormatter, mapFormatter, pieFormatter } from './formatters';
import { Empty, Fail, Loading } from './states';
import { ChartWrapper } from './style';
import { ConstructorType, Point, SizeType, StateType, Tooltip } from './types';

type Props = {
  options: Options;
  constructorType?: ConstructorType;
  state?: StateType;
  name?: string;
  onRefresh?: () => void;
  refreshMode?: 'debounce' | 'throttle';
};

const MAP_ASPECT_RATIO = 50 / 85;

const defaultOptions = {
  chart: {
    styledMode: true,
    marginTop: 75,
    spacing: [0, 0, 0, 0],
    colorCount: 6
  },
  title: {
    text: ''
  },
  credits: {
    enabled: false
  },
  exporting: {
    enabled: false
  },
  legend: {
    layout: 'horizontal',
    align: 'left',
    verticalAlign: 'top',
    float: true
  },
  tooltip: {
    borderRadius: 4,
    padding: 16,
    shape: 'square',
    useHTML: true,
    shared: true,
    formatter(tooltip: Tooltip) {
      let result;
      if (this.points) {
        if (this.points.every((point: Point) => point.series.type === 'column')) {
          // stacked barchart
          result = barFormatter.call(this, tooltip);
        } else {
          // barchart with single data serie
          result = graphicFormatter.call(this);
        }
      } else if (this.series && this.series.mapData) {
        // world map
        result = mapFormatter.call(this, tooltip);
      } else if (this.series.userOptions.type === 'pie') {
        // pie chart
        result = pieFormatter.call(this);
      } else {
        result = tooltip.defaultFormatter.call(this, tooltip);
      }
      return result;
    }
  }
};

/* настройки только для карты */
const getHighmapsOptions = (options: Options, size: SizeType) => {
  const mergedOptions: Options = _merge({}, defaultOptions, options);

  if (mergedOptions.legend) {
    mergedOptions.legend.verticalAlign = 'bottom';
  }

  // need to align legend with map
  mergedOptions.chart = _merge(
    mergedOptions.chart,
    {
      ...size,
      height: MAP_ASPECT_RATIO * (size.width || 0),
      marginTop: 0
    },
    options.chart
  );

  return mergedOptions;
};

const getHighchartsOptions = (options: Options, size: SizeType) => {
  const mergedOptions = _merge({}, defaultOptions, options);

  mergedOptions.chart = _merge(mergedOptions.chart, size, options.chart);

  // enabling stacking and labels for stacked columns
  if (mergedOptions.chart && mergedOptions.chart.type === 'column') {
    mergedOptions.plotOptions = _merge(
      {
        column: {
          stacking: 'normal'
        }
      },
      mergedOptions.plotOptions
    );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    _merge(mergedOptions.yAxis[0], {
      stackLabels: {
        enabled: true
      }
    });

    _merge(mergedOptions.xAxis, {
      labels: {
        format: '<b>{value}</b>'
      }
    });
  }

  if (mergedOptions.legend && mergedOptions.legend.enabled === false) {
    mergedOptions.chart.marginTop = 25;
  }

  if (!mergedOptions.series) {
    mergedOptions.series = [];
  }

  return mergedOptions;
};

const styleOptions = (options: Options, size: SizeType, constructorType: ConstructorType) =>
  constructorType === 'mapChart'
    ? getHighmapsOptions(options, size)
    : getHighchartsOptions(options, size);

const Charts = ({
  options,
  constructorType,
  state,
  onRefresh,
  refreshMode = 'debounce',
  name,
  ...props
}: Props): JSX.Element => {
  const highchartsConstructorType: ConstructorType = constructorType || 'chart';
  const highchartInstance = constructorType === 'mapChart' ? Highmaps : Highcharts;

  if (state === 'loading') {
    return <Loading data-test-id={`analytics-page__${name}__chart__loading`} />;
  }

  if (state === 'fail') {
    return <Fail data-test-id={`analytics-page__${name}__chart__fail`} />;
  }

  if (state === 'empty') {
    return <Empty data-test-id={`analytics-page__${name}__chart__empty`} />;
  }

  return (
    <ChartWrapper data-test-id={`analytics-page__${name}__chart`}>
      <ReactResizeDetector refreshMode={refreshMode} refreshRate={250}>
        {({ width, height }) => (
          <HighchartsReact
            highcharts={highchartInstance}
            options={styleOptions(options, { width, height }, highchartsConstructorType)}
            constructorType={highchartsConstructorType}
            {...props}
          />
        )}
      </ReactResizeDetector>
    </ChartWrapper>
  );
};

export default Charts;
